import type { ChordVariant, NoteEvent } from '@tunasong/music-lib'
import { getNote } from '@tunasong/music-lib'
import type { NoteName } from '@tunasong/schemas'
import { chordToEvents } from '../player/chord-to-events.js'
import { useStabScheduler } from '../player/stab-scheduler.js'

/**
 * Circle of Fifths v1 key-statement audition — two close-position triad
 * stabs (I-V for major, i-V for minor) at ~80 BPM dotted-half feel. The
 * minor-key audition uses a major V (with raised leading tone), the
 * canonical functional-tonal dominant choice per Bowie §5.
 *
 * Spec: docs/superpowers/specs/2026-05-23-cof-wheel-v1-design.md §6
 */
const TEMPO_BPM = 80
const BEAT_MS = 60_000 / TEMPO_BPM // 750 ms
const DOTTED_HALF_MS = BEAT_MS * 3 // 2250 ms
const GAP_MS = 250
const STEP_MS = DOTTED_HALF_MS + GAP_MS // 2500 ms

export interface CofKey {
  name: string
  ring: 'major' | 'minor'
}

export interface CofAuditionStab {
  events: NoteEvent[]
  delayMs: number
}

export const buildKeyAuditionChords = (key: CofKey): readonly [ChordVariant, ChordVariant] => {
  const tonic = key.name as NoteName
  const fifth = getNote(tonic, 'V')
  const tonicChord: ChordVariant =
    key.ring === 'minor' ? { root: tonic, variant: 'm' } : { root: tonic, variant: '' }
  const dominantChord: ChordVariant = { root: fifth, variant: '' }
  return [tonicChord, dominantChord]
}

export const buildKeyAuditionStabs = (key: CofKey): readonly CofAuditionStab[] => {
  const [i, v] = buildKeyAuditionChords(key)
  return [
    { events: chordToEvents(i, '2n.', 4), delayMs: 0 },
    { events: chordToEvents(v, '2n.', 4), delayMs: STEP_MS },
  ]
}

export interface UseCofKeyAuditionProps {
  key: CofKey | null
}

export const useCofKeyAudition = (props: UseCofKeyAuditionProps): { play: () => void; stop: () => void } => {
  const { play, stop } = useStabScheduler({
    buildStabs: () => (props.key ? [...buildKeyAuditionStabs(props.key)] : []),
  })
  return { play, stop }
}
